import React from 'react';
import { useT } from '../../i18n';

// Mirror of server/utils/password.js — kept in sync intentionally.
const COMMON = new Set([
  'password', 'password1', 'password123', '12345678', '123456789', 'qwerty123',
  'admin1234', 'letmein1', 'welcome1', 'iloveyou1', 'abc12345', 'monkey123',
]);

export const RULES = [
  { key: 'length',    test: p => p.length >= 8,            label: 'At least 8 characters' },
  { key: 'lowercase', test: p => /[a-z]/.test(p),          label: 'A lowercase letter' },
  { key: 'uppercase', test: p => /[A-Z]/.test(p),          label: 'An uppercase letter' },
  { key: 'digit',     test: p => /\d/.test(p),             label: 'A number' },
  { key: 'symbol',    test: p => /[^A-Za-z0-9]/.test(p),   label: 'A symbol (e.g. ! @ # $ %)' },
  { key: 'common',    test: p => p.length === 0 || !COMMON.has(p.toLowerCase()), label: 'Not a commonly-used password' },
];

export function checkPassword(pw = '') {
  const passed = RULES.filter(r => r.test(pw)).map(r => r.key);
  const failed = RULES.filter(r => !r.test(pw));
  return { passed, failed, valid: failed.length === 0, score: passed.length };
}

export default function PasswordStrength({ password = '', show = true }) {
  const { t } = useT();
  if (!show) return null;
  const { score, failed } = checkPassword(password);
  const total = RULES.length;

  // 0-2 weak (red), 3-4 medium (amber), 5 strong (blue), 6 excellent (green)
  const tier = password.length === 0 ? 0 : score <= 2 ? 1 : score <= 4 ? 2 : score === 5 ? 3 : 4;
  const colors = ['var(--border)', 'var(--danger)', 'var(--warning)', 'var(--blue)', 'var(--success)'];
  const labels = ['', t('password.weak'), t('password.fair'), t('password.strong'), t('password.excellent')];
  const barColor = colors[tier];

  return (
    <div style={{ marginTop: 6 }}>
      {/* Strength bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= tier ? barColor : 'var(--border)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>
      {password.length > 0 && (
        <div style={{ fontSize: 11, color: barColor, fontWeight: 600, marginBottom: 6 }}>
          {labels[tier]}
        </div>
      )}
      {/* Rules checklist */}
      {failed.length > 0 && password.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {RULES.map(r => {
            const ok = r.test(password);
            return (
              <li key={r.key} style={{
                fontSize: 11, color: ok ? 'var(--success)' : 'var(--text-subtle)',
                padding: '2px 0', display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ width: 10, display: 'inline-block', textAlign: 'center' }}>{ok ? '✓' : '○'}</span>
                {t(`password.${r.key}`)}
              </li>
            );
          })}
        </ul>
      )}
      {failed.length === 0 && password.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>✓</span> {t('password.meetsAll')}
        </div>
      )}
    </div>
  );
}
